// «План заказа» (Алексей 27.10 #2972): заметки к заказу на следующий день — чего не хватило гостям.
// Источники: звук у кассы («нет товара»), отзывы («не было еды»), голосом «Линза, добавь в план заказа …».
// Справочно, не отдельная функция: графа в Статусе и вкладка в Статистике. Советы (tip) — про
// неподключённые источники, с кнопкой туда, где подключать.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, Copy, Lightbulb, Mic, MessageSquareText, Star } from 'lucide-react';

import type { OrderPlanDto, OrderPlanItem, OrderPlanNoteKind } from '@/api/orderPlan';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type OrderPlanTip = { text: string; cta: string; to: string };

/** Советы по источникам: только отзывы → микрофон; только микрофон → отзывы; нет табличек с QR → табличка. */
export function orderPlanTips(s: OrderPlanDto['sources']): OrderPlanTip[] {
  const out: OrderPlanTip[] = [];
  if (!s.mic) out.push({ text: 'Подключите микрофон у кассы, чтобы собирать сюда, что гости спрашивали, а этого не было, и заметки голосом «Линза, добавь в план заказа…»', cta: 'Подключить микрофон', to: '/management/equipment' });
  if (!s.reviews_maps) out.push({ text: 'Подключите отзывы с карт — сюда попадут жалобы «не было еды»', cta: 'Подключить отзывы', to: '/management/reviews' });
  if (!s.review_form) out.push({ text: 'Поставьте таблички с QR для отзывов — гости сами напишут, чего не хватило', cta: 'Сделать табличку', to: '/management/reviews' });
  return out;
}

const KIND: Record<OrderPlanNoteKind, { icon: React.ElementType; label: string }> = {
  voice: { icon: Mic, label: 'попросили голосом' },
  speech: { icon: MessageSquareText, label: 'гость спросил у кассы' },
  review: { icon: Star, label: 'в отзыве' },
};

const when = (iso: string) => new Date(iso).toLocaleString('ru-RU', { weekday: 'short', hour: '2-digit', minute: '2-digit' });

export function OrderPlanTips({ tips }: { tips: OrderPlanTip[] }) {
  if (!tips.length) return null;
  return (
    <div className="space-y-1.5">
      {tips.map((tp) => (
        <div key={tp.text} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg bg-muted/50 px-3 py-2 text-sm">
          <Lightbulb className="size-4 shrink-0 text-primary" />
          <span className="min-w-0 flex-1"><span className="font-medium">Совет:</span> {tp.text}</span>
          <Link to={tp.to} className={buttonVariants({ variant: 'outline', size: 'sm' })}>{tp.cta}</Link>
        </div>
      ))}
    </div>
  );
}

export function OrderPlanBlock({ data, period }: { data: OrderPlanDto; period?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const text = [`Не хватило${period ? ` (${period})` : ''}:`, ...data.items.map((it) => `— ${it.name}${it.count > 1 ? ` (${it.count} раза)` : ''}`)].join('\n');
    try { await navigator.clipboard.writeText(text); setCopied(true); window.setTimeout(() => setCopied(false), 2000); } catch { /* буфер закрыт браузером */ }
  };
  return (
    <div className="space-y-2">
      {data.items.length === 0 ? (
        <div className="text-sm text-muted-foreground">За период гостям всего хватало — по крайней мере, по записям и отзывам.</div>
      ) : (
        <>
          <ul className="divide-y divide-border/50 rounded-lg border">
            {data.items.map((it) => <Row key={it.name + it.last_at} it={it} />)}
          </ul>
          <button type="button" onClick={copy} className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'gap-1.5')}>
            {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            {copied ? 'Скопировано' : 'Скопировать списком'}
          </button>
        </>
      )}
      <OrderPlanTips tips={orderPlanTips(data.sources)} />
    </div>
  );
}

function Row({ it }: { it: OrderPlanItem }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="px-3 py-2">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 text-left text-sm">
        <span className="min-w-0 flex-1 font-medium first-letter:uppercase">{it.name}</span>
        <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
          {it.kinds.map((k) => { const K = KIND[k].icon; return <K key={k} className="size-3.5" aria-label={KIND[k].label} />; })}
        </span>
        {it.count > 1 && <span className="shrink-0 rounded-full bg-muted px-1.5 text-xs tabular-nums">×{it.count}</span>}
        <span className="w-20 shrink-0 text-right text-xs text-muted-foreground">{when(it.last_at)}</span>
        <ChevronDown className={cn('size-4 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1.5 pl-1">
          {it.notes.map((n, i) => {
            const K = KIND[n.kind].icon;
            const link = n.conversation_id ? `/management/speech?conv=${n.conversation_id}` : n.review_id ? '/management/analytics?tab=reviews' : null;
            return (
              <li key={i} className="flex gap-2 text-xs">
                <K className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <div className="text-muted-foreground">{when(n.at)} · {KIND[n.kind].label}{n.text && n.kind !== 'voice' ? ` · ${n.text}` : ''}</div>
                  {n.quote && <div className="italic">«{n.quote}»</div>}
                </div>
                {link && <Link to={link} className="shrink-0 text-primary underline-offset-2 hover:underline">{n.conversation_id ? 'Послушать' : 'Отзывы'}</Link>}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
